import { useEffect, useState } from "react";
import { getXeroShortcode } from "./api";
import type { Client } from "./api";
import { extensionVersion, syncClients } from "./extension";

/**
 * Keep the Casa extension's client list in step with BAS.
 *
 * Every Xero-connected client goes to the extension with its Xero short code,
 * so staff never type one. A client whose short code BAS has not looked up
 * yet is looked up here, one at a time; after that it is kept and costs
 * nothing. Does nothing when the extension is not installed.
 *
 * Returns how many clients the extension now holds from BAS, or null.
 */
export function useExtensionSync(clients: Client[] | null, token: string | undefined): number | null {
  const [synced, setSynced] = useState<number | null>(null);

  useEffect(() => {
    if (!clients || !token) return;
    let live = true;

    void (async () => {
      if ((await extensionVersion()) === null) return;
      const list: { shortcode: string; name: string }[] = [];
      for (const client of clients) {
        if (!live) return;
        if (!client.xero.connected) continue;
        let code = client.xero.short_code || "";
        if (!code) {
          try {
            // One at a time on purpose: each may refresh a Xero token, and
            // Xero rate-limits per app. It happens once per client, ever.
            // oxlint-disable-next-line no-await-in-loop
            code = (await getXeroShortcode(token, client.client_id)).short_code;
          } catch {
            continue; // Xero unreachable for this one; it is picked up next time.
          }
        }
        list.push({ shortcode: code, name: client.name });
      }
      const count = await syncClients(list);
      if (live) setSynced(count);
    })();

    return () => {
      live = false;
    };
  }, [clients, token]);

  return synced;
}
